import api from "../../../conf/api";

/**
 * EditorServices for the embedded video editor (@wooglobe/vre-editor).
 *
 * Every call goes through CreatorVault's authenticated `api` to the backend's
 * `/mrss/editor/<path>` passthrough, which forwards to the VRE API with the
 * server-held key — VRE credentials never reach the browser.
 *
 * Mirrors VRE's own HttpApi (frontend/src/api/http.ts) method by method: same
 * arguments, same return shapes, same generous timeouts for slow work (uploads,
 * AI writing, speech and music generation).
 */
const BASE = "/mrss/editor";

const TIMEOUT = {
  fontUpload: 60000,
  fontFile: 60000,
  soundUpload: 120000,
  mediaUpload: 600000,
  writing: 120000,
  speech: 150000,
  speechBulk: 600000,
  music: 150000,
};

/** An Error whose message is what the server said, so editor panels show it. */
function toError(err) {
  const data = err?.response?.data;
  const message = data?.detail || data?.error || err?.message || "Request failed";
  const error = new Error(typeof message === "string" ? message : JSON.stringify(message));
  error.status = err?.response?.status ?? 0;
  return error;
}

async function unwrap(request) {
  try {
    const res = await request;
    return res.data;
  } catch (err) {
    throw toError(err);
  }
}

const withTimeout = (timeout, config = {}) => (timeout ? { ...config, timeout } : config);

const get = (path, timeout) => unwrap(api.get(`${BASE}/${path}`, withTimeout(timeout)));
const post = (path, body, timeout) => unwrap(api.post(`${BASE}/${path}`, body, withTimeout(timeout)));
const del = (path) => unwrap(api.delete(`${BASE}/${path}`));

// ── Fonts ──────────────────────────────────────────────────────────────────
// VRE serves bundled fonts from `/api/fonts/file/<family>`, which needs auth.
// `FontFace` cannot send an Authorization header, so the bytes are fetched
// through the passthrough and handed to the editor as a `blob:` URL. Cached
// per family for the page's lifetime; a failed fetch is not cached, so a later
// listFonts() retries it. Uploaded fonts carry absolute media URLs and pass
// through untouched.
const VRE_FONT_FILE = /\/api\/fonts\/file\/([^?#]+)/;
const fontBlobUrls = new Map();

function fontBlobUrl(familySegment) {
  let pending = fontBlobUrls.get(familySegment);
  if (!pending) {
    pending = api
      .get(`${BASE}/fonts/file/${familySegment}`, { responseType: "blob", timeout: TIMEOUT.fontFile })
      .then((res) => URL.createObjectURL(res.data))
      .catch(() => {
        fontBlobUrls.delete(familySegment);
        return "";
      });
    fontBlobUrls.set(familySegment, pending);
  }
  return pending;
}

async function withBrowserFontUrl(font) {
  const match = typeof font?.url === "string" ? font.url.match(VRE_FONT_FILE) : null;
  return match ? { ...font, url: await fontBlobUrl(match[1]) } : font;
}

const fileForm = (file, fields = {}) => {
  const form = new FormData();
  form.append("file", file);
  Object.entries(fields).forEach(([key, value]) => {
    if (value) form.append(key, value);
  });
  return form;
};

export function createEditorServices() {
  return {
    // ---------------------------------------------------------------- fonts
    async listFonts() {
      const fonts = await get("fonts");
      return Promise.all((Array.isArray(fonts) ? fonts : []).map(withBrowserFontUrl));
    },

    async uploadFont(file) {
      const saved = await post("fonts", fileForm(file), TIMEOUT.fontUpload);
      return {
        family: saved.family,
        source: "upload",
        weights: [saved.weight],
        has_italic: false,
        url: saved.url,
      };
    },

    // --------------------------------------------------------------- sounds
    async listSounds(query = {}) {
      const params = new URLSearchParams();
      if (query.kind) params.set("kind", query.kind);
      if (query.category) params.set("category", query.category);
      if (query.q) params.set("q", query.q);
      if (query.limit) params.set("limit", String(query.limit));
      if (query.origin) params.set("origin", query.origin);
      const qs = params.toString();
      return get(`sounds${qs ? `?${qs}` : ""}`);
    },

    async uploadSound(file, kind = "sfx", category = "") {
      return post("sounds", fileForm(file, { kind, category }), TIMEOUT.soundUpload);
    },

    async deleteSound(id) {
      await del(`sounds/${encodeURIComponent(id)}`);
    },

    // ---------------------------------------------------------------- media
    async uploadMedia(file) {
      return post("media", fileForm(file), TIMEOUT.mediaUpload);
    },

    // -------------------------------------------------------------- writing
    async captionPrompt() {
      return get("writing/captions/prompt");
    },

    async generateCaptions(req) {
      const body = await post("writing/captions", req, TIMEOUT.writing);
      return body.captions;
    },

    async generateStory(req) {
      const body = await post("writing/story", req, TIMEOUT.writing);
      return body.story;
    },

    // ---------------------------------------------------------------- voice
    async listVoices() {
      return get("voice/voices");
    },

    async generateSpeech(text, voiceId) {
      return post("voice/speech", { text, ...(voiceId ? { voice_id: voiceId } : {}) }, TIMEOUT.speech);
    },

    async generateSpeechBulk(items, voiceId) {
      return post(
        "voice/speech/bulk",
        { items, ...(voiceId ? { voice_id: voiceId } : {}) },
        TIMEOUT.speechBulk,
      );
    },

    async generateMusic(prompt, durationSeconds = 15) {
      return post("voice/music", { prompt, duration: durationSeconds }, TIMEOUT.music);
    },
  };
}
